import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import logo from "../../assets/images/Logo.png";
import TelecallerNavbar from "../Navbar/TelecallerNavbar";
import { logOutUser } from "../../store/slices/authSlice";
import useOutsideClick from "../Assets/useOutsideClick";
import { getTaskNotificationCount } from "../../store/slices/taskmanagementSlice";
import TaskManagerNotificationBell from "../TaskManagement/TaskManagerNotificationBell";
import { NavLinkItem } from "../../models/routes.model";

const MainNavbar: React.FC<{ role: string }> = ({ role }) => {
  const location = useLocation();
  const [isNavDropDownOpen, setIsNavDropDownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const navDropdownRef = useRef<HTMLLIElement>(null);
  const dispatch: RootDispatch = useDispatch();

  const userName = useSelector((state: RootState) => state.auth.userName);
  const currentConfig = useSelector(
    (state: RootState) => state.routesSlice.nav,
  );
  const taskNotification =
    useSelector((state: RootState) => state.TaskManagement.notificationCount) ??
    0;

  useEffect(() => {
    setIsNavDropDownOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    dispatch(getTaskNotificationCount());
  }, [dispatch]);

  const handleLogout = () => {
    dispatch(logOutUser());
  };

  useOutsideClick(userDropdownRef, () => setIsUserDropdownOpen(false));
  useOutsideClick(navDropdownRef, () => {
    if (window.innerWidth >= 1024) setIsNavDropDownOpen(false);
  });

  const toggleNavDropdown = () => setIsNavDropDownOpen((prev) => !prev);

  // Active link classes
  const linkClass = (isActive: boolean) =>
    `relative rounded-lg px-3 py-2 text-[11px] xl:text-xs font-medium transition-all duration-200 block whitespace-nowrap ${
      isActive
        ? "bg-navyBlue-primary text-white shadow-sm"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    }`;

  return (
    <>
      {/* MOBILE MENU */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}
      <div
        className={`fixed inset-y-0 left-0 z-[120] flex w-72 flex-col border-r border-slate-200 bg-white shadow-2xl transform transition-transform duration-300 lg:hidden ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} className="w-6 h-6" alt="Frelit" />
            <span className="font-semibold">Frelit</span>
          </Link>
          <button onClick={() => setIsMobileMenuOpen(false)}>✖</button>
        </div>

        {/* User Welcome */}
        <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
          <div>
            Welcome,&nbsp;
            <span className="font-semibold text-blue-600">
              {userName?.username?.capitalizeWords()}
            </span>
          </div>
        </div>

        {/* MAIN LINKS */}
        <div className="flex-1 overflow-y-auto">
          <ul className="space-y-1 p-3">
            {(currentConfig?.main as NavLinkItem[])?.map((link) => (
              <li key={link.path}>
                <NavLink
                  to={link.path}
                  end={link.path === "/"}
                  className={({ isActive }) => linkClass(isActive)}
                >
                  <span className="flex items-center gap-2">
                    {link.label}
                    {link.label === "Task Manager" && taskNotification > 0 && (
                      <span className="bg-red-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                        {taskNotification}
                      </span>
                    )}
                  </span>
                </NavLink>
              </li>
            ))}

            {/* Frelit */}
            {currentConfig?.frelit &&
              (currentConfig?.frelit as NavLinkItem[])?.length > 0 && (
                <li ref={navDropdownRef}>
                  <div
                    className={linkClass(
                      location.pathname.startsWith("/frelit"),
                    )}
                    onClick={toggleNavDropdown}
                  >
                    Frelit
                  </div>
                  {isNavDropDownOpen && (
                    <ul className="mt-1 ml-3 space-y-1 z-100">
                      {(currentConfig?.frelit as NavLinkItem[]).map((link) => (
                        <li key={link.path}>
                          <NavLink
                            to={link.path}
                            className={({ isActive }) => linkClass(isActive)}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {link.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )}

            {/* EXTRAS */}
            {currentConfig?.extras &&
              (currentConfig?.extras as NavLinkItem[])?.length > 0 && (
                <li ref={navDropdownRef}>
                  <div
                    className={linkClass(
                      location.pathname.startsWith("/extras"),
                    )}
                    onClick={toggleNavDropdown}
                  >
                    Extras
                  </div>
                  {isNavDropDownOpen && (
                    <ul className="mt-1 ml-3 space-y-1">
                      {(currentConfig?.extras as NavLinkItem[])?.map((link) => (
                        <li key={link.path}>
                          <NavLink
                            to={link.path}
                            className={({ isActive }) => linkClass(isActive)}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {link.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )}
          </ul>
        </div>

        {/* LOGOUT */}
        <div className="border-t p-3">
          <button
            onClick={handleLogout}
            className="w-full p-2.5 rounded-lg hover:bg-red-50 hover:text-red-600"
          >
            Logout
          </button>
        </div>
      </div>

      {/* DESKTOP NAV */}
      {role === "telecaller" ? (
        <TelecallerNavbar />
      ) : (
        <div className="sticky top-0 z-[90] border-b border-slate-200/70 bg-white/85 backdrop-blur-md supports-[backdrop-filter]:bg-white/70 shadow-sm transition-all duration-300">
          <div className="mx-auto flex h-14 items-center justify-between px-3 lg:px-4">
            {/* Logo */}
            <Link to="/" className="flex items-center ">
              <img src={logo} className="w-4 h-4" alt="Frelit" />
            </Link>

            {/* Main Links */}
            <ul className="hidden lg:flex items-center gap-1">
              {(currentConfig?.main as NavLinkItem[])?.map((link) => (
                <li key={link.path}>
                  <NavLink
                    to={link.path}
                    end={link.path === "/"}
                    className={({ isActive }) => linkClass(isActive)}
                  >
                    {({ isActive }) => (
                      <span className="flex items-center gap-2 relative">
                        {link.label}
                        {link.label === "Task Manager" &&
                          taskNotification! > 0 &&
                          !isActive && (
                            <span className="absolute -top-3.5 -right-4 bg-red-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                              {taskNotification}
                            </span>
                          )}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}

              {/* Extras */}
              {currentConfig?.extras &&
                (currentConfig?.extras as NavLinkItem[])?.length > 0 && (
                  <li ref={navDropdownRef} className="relative">
                    <div
                      onClick={toggleNavDropdown}
                      className="flex cursor-pointer items-center gap-1 rounded-lg px-3 py-2 text-[11px] xl:text-xs font-medium text-slate-600 transition-all duration-200 hover:bg-slate-100 hover:text-slate-900"
                    >
                      <span>Extras</span>
                      <span className="text-[10px]">▼</span>
                    </div>
                    {isNavDropDownOpen && (
                      <ul className="absolute left-0 top-full z-[95] mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl backdrop-blur-md">
                        {(currentConfig?.extras as NavLinkItem[])?.map(
                          (link) => (
                            <li key={link.path}>
                              <NavLink
                                to={link.path}
                                className={({ isActive }) =>
                                  linkClass(isActive)
                                }
                                onClick={() => setIsNavDropDownOpen(false)}
                              >
                                {link.label}
                              </NavLink>
                            </li>
                          ),
                        )}
                      </ul>
                    )}
                  </li>
                )}
            </ul>

            {/* Right Actions */}
            <div className="flex items-center gap-2 lg:gap-3">
              <button
                className="rounded-lg p-2 transition hover:bg-slate-100 lg:hidden"
                onClick={() => setIsMobileMenuOpen(true)}
              >
                ☰
              </button>
              <TaskManagerNotificationBell />
              <div
                ref={userDropdownRef}
                className="relative hidden lg:flex items-center space-x-2 text-sm"
              >
                <button
                  onClick={() => setIsUserDropdownOpen((v) => !v)}
                  className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 transition hover:bg-slate-50"
                >
                  <span>{userName?.username?.capitalizeWords()}</span>
                  <span>▼</span>
                </button>

                {isUserDropdownOpen && (
                  <ul className="absolute right-0 z-[95] mt-2 w-36 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">
                    <li>
                      <button
                        onClick={handleLogout}
                        className="w-full px-3 py-2 hover:bg-red-50 hover:text-red-600"
                      >
                        Logout
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MainNavbar;
